import { SuggestionOptions } from '@tiptap/suggestion'
import { apiV3, defaultUserGroupsSelector } from '@opallabs/api'
import { DocumentResourceType } from '../types'

const MENTION_DEBOUNCE_MS = 200

const delay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms))

export const getMentions = (
  resourceType?: DocumentResourceType,
  resourceId?: string
): SuggestionOptions['items'] => {
  let requestToken = 0

  const fetchMentionCandidates = async (term: string) => {
    const [users, userGroupsDocument] = await Promise.all([
      apiV3.users.readCollection({
        query: {
          filter: {
            name: term,
            is_active: true,
          },
          page: {
            limit: 6,
          },
        },
      }),
      apiV3.userGroups.readCollection({
        params: {
          query: {
            filter: {
              name: term,
            },
          },
        },
      }),
    ])

    const groups = defaultUserGroupsSelector(
      userGroupsDocument,
      false // `showArchived: false` to only return active groups
    )

    const combined = [...groups.slice(0, 3), ...users.data.slice(0, 6)]
    const final = combined.slice(0, 6)

    return final.map((u) => ({
      id: u.type === 'user' ? u.id : u.uuid,
      label: u.type === 'user' ? u.attributes.full_name : u.name,
      resource_type: u.type === 'user' ? 'user' : 'group',
      document_resource_type: resourceType,
      document_resource_id: resourceId,
    }))
  }

  return async ({ query }) => {
    const trimmedQuery = query?.trim() ?? ''
    const token = ++requestToken

    if (trimmedQuery.length === 0) {
      return []
    }

    await delay(MENTION_DEBOUNCE_MS)

    if (token !== requestToken) {
      return []
    }

    const mentions = await fetchMentionCandidates(trimmedQuery)

    if (token !== requestToken) {
      return []
    }

    return mentions
  }
}
